import type { ApolloError } from "@apollo/client";
import { type ReactNode } from "react";

import WebApiClient from "@/api/WebApiClient.ts";
import { notify } from "@/modules/notifications/notify";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import {
  AUTHENTICATION_EXCEPTION,
  AUTHORISATION_EXCEPTION,
  DATA_FETCHING_EXCEPTION,
  INTERNAL_SERVER_ERROR,
  LICENSE_SEATS_EXCEPTION,
  MODULE_NOT_ENABLED_EXCEPTION,
  NOT_FOUND_EXCEPTION,
  SERVICE_UNAVAILABLE_EXCEPTION,
  VALIDATION_ERROR,
} from "@/settings/settings.ts";

export interface ErrorHandlerProps {
  actionType?: string;
  actionPayload?: unknown;
  showAlert?: boolean;
  alertTitle?: string;
  alertMessage?: ReactNode;
  throwError?: boolean; // if we need to pass the error through again to be caught downstream e.g. for redux-form
  rejectPromise?: boolean;
}

export function createErrorHandler(
  dispatch: ReduxDispatch,
  errorHandlerProps: ErrorHandlerProps,
): (error: ApolloError) => Promise<unknown> {
  return (error): Promise<unknown> => {
    let showAlert = errorHandlerProps.showAlert !== false;
    let specificErrorMessage: string | undefined;
    let reject: Promise<unknown> | undefined;
    if (error.networkError) {
      // A Notification is shown from the middleware
      return Promise.reject({ ...error, message: WebApiClient.NETWORK_ERROR_MESSAGE });
    }
    // Handling first found authentication GraphQL exception differently.
    // It shouldn't relate on `throwError` and no notification (`showAlert`)
    // should be shown.
    const authenticationException = error.graphQLErrors?.find((err) => err.type === AUTHENTICATION_EXCEPTION);
    if (authenticationException) {
      showAlert = false;
    } else if (!errorHandlerProps.throwError && error.graphQLErrors) {
      // if error contains the property {{graphQLErrors}} it means that the graphQL response was in the form
      // { data: {}, errors: [{ message: ...}] } and has been converted by the ApolloClient ;
      // the throwError is in general to let the form handle the error, in which case we should not show the general error page
      error.graphQLErrors.forEach((graphQLError) => {
        if (
          graphQLError.type === NOT_FOUND_EXCEPTION ||
          graphQLError.type === INTERNAL_SERVER_ERROR ||
          graphQLError.type === AUTHORISATION_EXCEPTION ||
          graphQLError.type === MODULE_NOT_ENABLED_EXCEPTION ||
          graphQLError.type === LICENSE_SEATS_EXCEPTION
        ) {
          //! Managed at the middleware level. @see @/setup.ts
          showAlert = false;
          return;
        } else if (graphQLError.type === VALIDATION_ERROR || graphQLError.type === SERVICE_UNAVAILABLE_EXCEPTION) {
          specificErrorMessage = graphQLError.message; // will allow to override any front-end generic error message in case a specific message is provided by the server
        } else if (graphQLError.type === DATA_FETCHING_EXCEPTION) {
          // we also have to deal with this type of error
          showAlert = false;
          reject = Promise.reject({
            type: errorHandlerProps.actionType,
            payload: errorHandlerProps.actionPayload,
            error: error,
          });
        } else {
          if (import.meta.env.DEV) {
            console.error("Unmanaged GraphQL Error:", error);
          }
        }
      });
    }

    if (errorHandlerProps.actionType) {
      // dispatch the action further as originally intended (this method acts as a redux middleware)
      const errorContainer = {
        type: errorHandlerProps.actionType,
        payload: errorHandlerProps.actionPayload,
        error: error,
      };
      dispatch(errorContainer);

      if (errorHandlerProps.rejectPromise) {
        return reject ?? Promise.reject(errorContainer);
      }
    }

    if (showAlert) {
      const title = errorHandlerProps.alertTitle ? errorHandlerProps.alertTitle : "An Error Occurred";
      const message =
        specificErrorMessage || errorHandlerProps.alertMessage || cleanupGraphQLErrorMessage(error.message);

      notify.error({ title: title, children: message, seeMoreDetailsLink: true }, { toastId: title });
    }

    if (errorHandlerProps.throwError) {
      // sometimes we do not want the error to be thrown again since we want the rest of the logic to continue despite the exception (e.g. loop of downloads)
      throw error;
    }

    return Promise.reject("An error occurred.");
  };
}

// only used in WebApiClient in a generic way
export function cleanupGraphQLErrorMessage(errorMessage: string): string {
  return errorMessage ? errorMessage.replace("GraphQL error:", "") : "";
}
